import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePendingAcknowledgements } from '@/hooks/usePendingAcknowledgements';
import { useUserRole } from '@/hooks/useUserRole';
import { useSubscriptionStatus } from '@/hooks/useSubscriptionStatus';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  skipAcknowledgementCheck?: boolean;
}

export function ProtectedRoute({ children, skipAcknowledgementCheck = false }: ProtectedRouteProps) {
  const { user, profile, loading, profileError } = useAuth();
  const location = useLocation();
  const { data: pendingAcks, isLoading: acksLoading } = usePendingAcknowledgements();
  const { data: userRole, isLoading: roleLoading } = useUserRole();
  const { data: subscriptionStatus, isLoading: statusLoading, isError: statusError } = useSubscriptionStatus();

  if (loading || acksLoading || roleLoading || statusLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!user) {
    const intended = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth?redirect=${intended}`} replace />;
  }

  // Authenticated but no profile - show blocked message
  if (profileError || !profile) {
    return <Navigate to="/no-access" replace />;
  }

  // On a genuine status read failure (network blip), do NOT redirect — render
  // through and let the server-side write guards enforce Soft-Close. The route
  // guard is navigation, not the lock (spec §4.2); failing it open on error
  // never grants writes, but failing it closed would bounce a paying clinic out
  // of its own records (the OWASP A04 lockout the spec forbids, §2).
  const isSuperAdmin = profile?.is_super_admin === true;
  const status = subscriptionStatus?.status ?? null;
  const isSoftClosed = status === 'soft_closed';
  const needsOnboarding =
    status === null || status === 'incomplete' || status === 'paused';

  if (!isSuperAdmin && !statusError && needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  // Role-based dashboard routing
  if (userRole === 'staff' && location.pathname === '/dashboard') {
    return <Navigate to="/staff-dashboard" replace />;
  }
  if (userRole !== 'staff' && location.pathname === '/staff-dashboard') {
    return <Navigate to="/dashboard" replace />;
  }

  // Soft-closed clinics skip the acknowledgement gate — no new ack work is
  // created for them (write-blocked server-side), and they must reach their
  // read-only dashboard rather than being trapped on /pending-acknowledgements.
  const isStaffDashboard = userRole === 'staff' && location.pathname === '/staff-dashboard';
  if (!skipAcknowledgementCheck && !isSoftClosed && !isStaffDashboard && pendingAcks && pendingAcks.length > 0) {
    return <Navigate to="/pending-acknowledgements" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}
